import { useState, useEffect, useCallback, useRef } from 'react';
import shermanSrc from '../assets/builder_sherman.png';

const LOGIN_STEPS = [
  {
    id: 'login-welcome',
    highlight: 'login-form',
    text: "Hey there! I'm Sherman. Looks like you need to log in, enter your server's SFTP details on the left. You can find them in your game panel under the File Manager tab.",
    options: { dismiss: "Got it", guide: "Why use SFTP?" }
  },
  {
    id: 'login-why-sftp',
    highlight: null,
    text: "Great question! The built-in file manager is fine for small edits, but SFTP has no file size limits, handles bulk uploads way faster, and won't time out on large transfers. Think of this as a full IDE for your server files.",
    options: { dismiss: "Makes sense!" }
  }
];

const DESKTOP_LOGIN_STEPS = [
  {
    id: 'desktop-login-welcome',
    highlight: 'login-form',
    text: "Hey! I'm Sherman. Enter your server's hostname, port, username, and password to connect. You can save logins for quick access later!",
    options: { dismiss: "Got it", guide: "Why use SFTP?" }
  },
  {
    id: 'desktop-login-why-sftp',
    highlight: null,
    text: "SFTP has no file size limits, handles bulk uploads way faster than a web file manager, and won't time out on large transfers. It's the best way to manage server files.",
    options: { dismiss: "Makes sense!" }
  }
];

const TRANSFER_LOGIN_STEPS = [
  {
    id: 'transfer-login',
    highlight: 'login-form',
    text: "Hey! I'm Sherman. Looks like you're moving servers, nice! First, log in with your Kinetic server details. Your password is the same as your game panel account.",
    options: { dismiss: "Got it" }
  }
];

const TOUR_STEPS = [
  {
    id: 'welcome',
    page: null,
    highlight: null,
    text: "Welcome in! I'm Sherman, your friendly guide. Want a quick tour of everything?",
    options: { dismiss: "No thanks", guide: "Show me around!" }
  },
  {
    id: 'tabs',
    page: 'edit',
    highlight: 'tabs',
    text: "Up here are your tabs, you can open multiple server connections at once. Click the + to add a new tab, just like a browser."
  },
  {
    id: 'action-bar',
    page: 'edit',
    highlight: 'action-bar',
    text: "This is your action bar. Upload files, create folders, refresh the file list, and toggle multi-select, all your quick actions live here."
  },
  {
    id: 'view-tabs',
    page: 'edit',
    highlight: 'view-tabs',
    text: "These tabs switch between views, File View for editing, Transfer for moving files between servers, and Settings to customize everything."
  },
  {
    id: 'files',
    page: 'edit',
    highlight: 'files',
    text: "This is your File View, the main workspace! Your server files are on the left. Click any file to open it in the editor on the right."
  },
  {
    id: 'editor',
    page: 'edit',
    highlight: 'editor',
    text: "The editor has syntax highlighting and a special config mode for common file types. Hit Save when you're done, easy as that!"
  },
  {
    id: 'transfer',
    page: 'transfer',
    highlight: null,
    text: "The Transfer tab is great for moving between servers, whether you're migrating from another host or moving between Kinetic servers. Connect your other server and hit Copy!"
  },
  {
    id: 'settings',
    page: 'settings',
    highlight: null,
    text: "And here's Settings, transfer limits, saved logins, all the good stuff. Make it yours!"
  },
  {
    id: 'done',
    page: 'edit',
    highlight: null,
    text: "That's the full tour! You're all set. Now go build something awesome!",
    options: { dismiss: "Thanks, Sherman!" }
  }
];

const TRANSFER_TOUR_STEPS = [
  {
    id: 'transfer-welcome',
    page: 'transfer',
    highlight: 'transfer-current',
    text: "You're on the Transfer page! Your Kinetic server is on the right, that's already connected.",
    options: { dismiss: "I know what I'm doing", guide: "Walk me through it" }
  },
  {
    id: 'transfer-other',
    page: 'transfer',
    highlight: 'transfer-other',
    text: "On the left side, enter the SFTP login details for your old host, the server you're moving from. Hit Connect once they're filled in."
  },
  {
    id: 'transfer-all',
    page: 'transfer',
    highlight: 'transfer-all',
    text: "The easiest way? Just hit Transfer All! It'll copy every file from your old server straight over in one go."
  },
  {
    id: 'transfer-select',
    page: 'transfer',
    highlight: 'transfer-files-other',
    text: "Or if you only want certain files, use the checkboxes to tick the ones you need, then hit the Copy button in the middle to move them across."
  },
  {
    id: 'transfer-copy',
    page: 'transfer',
    highlight: 'transfer-copy',
    text: "These Copy buttons move your selected files between servers. Left arrow sends to your old server, right arrow brings them to your Kinetic server."
  },
  {
    id: 'transfer-done',
    page: 'transfer',
    highlight: null,
    text: "That's it! Keep this tab open while transfers run. Once done, switch to File View to browse or edit your files.",
    options: { dismiss: "Thanks, Sherman!" }
  }
];

const SORRY_MESSAGES = [
  "Sorry about that! Where were we...",
  "I'm back! Sorry for wandering off...",
  "Oops, got distracted! Anyway...",
   "Sorry, had to drop this box off quick",
];

const STORAGE_KEY = 'kinetic-sherman-dismissed';
const LOGIN_STORAGE_KEY = 'kinetic-sherman-login-dismissed';
const TYPE_SPEED = 25;
const HIGHLIGHT_COLOR = '#f5a623';
const IDLE_INTERVAL = 8000;
const SORRY_DISPLAY_TIME = 3500;

const SIMPLE_IDLES = [
  { cls: 'sherman-idle-spin', dur: 800 },
  { cls: 'sherman-idle-hop', dur: 1600 },
  { cls: 'sherman-idle-wobble', dur: 1400 },
];

const useIdleAnimation = (textDone) => {
  const [idleClass, setIdleClass] = useState('');
  const [isGone, setIsGone] = useState(false);
  const [showSorry, setShowSorry] = useState(false);
  const timerRef = useRef(null);
  const phaseRef = useRef(null);
  const sorryRef = useRef(null);

  const clearTimers = () => {
    clearTimeout(timerRef.current);
    clearTimeout(phaseRef.current);
    clearTimeout(sorryRef.current);
  };

  const doReturn = useCallback((scheduleNext) => {
    clearTimers();
    const sorry = SORRY_MESSAGES[Math.floor(Math.random() * SORRY_MESSAGES.length)];
    sorryRef.current = sorry;
    setShowSorry(true);
    setIdleClass('sherman-idle-walk-in');
    setIsGone(false);
    phaseRef.current = setTimeout(() => {
      setIdleClass('');
      phaseRef.current = setTimeout(() => {
        setShowSorry(false);
        if (scheduleNext) scheduleNext();
      }, SORRY_DISPLAY_TIME);
    }, 800);
  }, []);

  const recall = useCallback(() => {
    if (!isGone) return;
    doReturn(null);
  }, [isGone, doReturn]);

  useEffect(() => {
    if (!textDone) {
      setIdleClass('');
      setIsGone(false);
      setShowSorry(false);
      clearTimers();
      return;
    }

    const scheduleIdle = () => {
      timerRef.current = setTimeout(() => {
        const doWander = Math.random() < 0.25;

        if (doWander) {
          setIdleClass('sherman-idle-walk-out');
          phaseRef.current = setTimeout(() => {
            setIdleClass('sherman-idle-gone');
            setIsGone(true);
            phaseRef.current = setTimeout(() => {
              doReturn(scheduleIdle);
            }, 20000);
          }, 800);
        } else {
          const pick = SIMPLE_IDLES[Math.floor(Math.random() * SIMPLE_IDLES.length)];
          setIdleClass(pick.cls);
          phaseRef.current = setTimeout(() => {
            setIdleClass('');
            scheduleIdle();
          }, pick.dur);
        }
      }, IDLE_INTERVAL);
    };

    scheduleIdle();
    return () => clearTimers();
  }, [textDone, doReturn]);

  const sorryMessage = showSorry ? (sorryRef.current || SORRY_MESSAGES[0]) : null;

  return { idleClass, isGone, showSorry, sorryMessage, recall };
};

const useHighlight = (targetId) => {
  useEffect(() => {
    if (!targetId) return;
    const el = document.querySelector(`[data-sherman="${targetId}"]`);
    if (!el) return;

    const origOutline = el.style.outline;
    const origOutlineOffset = el.style.outlineOffset;
    const origTransition = el.style.transition;
    const origZIndex = el.style.zIndex;
    const origPosition = el.style.position;

    el.style.transition = 'outline-color 0.4s ease-in-out';
    el.style.outlineOffset = '3px';
    el.style.zIndex = '40';
    if (!el.style.position || el.style.position === 'static') {
      el.style.position = 'relative';
    }

    let on = true;
    el.style.outline = `3px solid ${HIGHLIGHT_COLOR}`;
    const interval = setInterval(() => {
      on = !on;
      el.style.outline = on ? `3px solid ${HIGHLIGHT_COLOR}` : '3px solid transparent';
    }, 600);

    return () => {
      clearInterval(interval);
      el.style.outline = origOutline;
      el.style.outlineOffset = origOutlineOffset;
      el.style.transition = origTransition;
      el.style.zIndex = origZIndex;
      el.style.position = origPosition;
    };
  }, [targetId]);
};

const TypewriterText = ({ text, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    indexRef.current = 0;
    const tick = () => {
      indexRef.current += 1;
      const next = text.slice(0, indexRef.current);
      setDisplayed(next);
      if (indexRef.current >= text.length) {
        setDone(true);
        if (onDone) onDone();
      } else {
        timerRef.current = setTimeout(tick, TYPE_SPEED);
      }
    };
    timerRef.current = setTimeout(tick, TYPE_SPEED);
    return () => clearTimeout(timerRef.current);
  }, [text]);

  const skipToEnd = () => {
    clearTimeout(timerRef.current);
    setDisplayed(text);
    setDone(true);
    if (onDone) onDone();
  };

  return (
    <p className="sherman-text" onClick={skipToEnd} style={{ cursor: done ? 'default' : 'pointer' }}>
      {displayed}
      {!done && <span className="sherman-cursor">|</span>}
    </p>
  );
};

const SHERMAN_FONT_STACK = "'Lucida Sans Typewriter', 'Lucida Console', 'Courier New', monospace";

const LoginSherman = ({ isTransferMode, isDesktopMode }) => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [textDone, setTextDone] = useState(false);
  const { idleClass, isGone, showSorry, sorryMessage, recall } = useIdleAnimation(textDone);

  const steps = isTransferMode ? TRANSFER_LOGIN_STEPS : isDesktopMode ? DESKTOP_LOGIN_STEPS : LOGIN_STEPS;

  useEffect(() => {
    try {
      if (localStorage.getItem(LOGIN_STORAGE_KEY)) return;
    } catch { /* */ }
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { setTextDone(false); }, [step]);

  const dismiss = useCallback(() => {
    recall();
    setVisible(false);
    try { localStorage.setItem(LOGIN_STORAGE_KEY, '1'); } catch { /* */ }
  }, [recall]);

  const showWhySftp = useCallback(() => {
    recall();
    setStep(1);
  }, [recall]);

  const current = steps[step];
  useHighlight(visible ? current.highlight : null);

  if (!visible) return null;

  const bubbleText = isGone ? '...' : showSorry ? sorryMessage : null;

  return (
    <div
      className="fixed z-50 flex items-end gap-4"
      style={{ bottom: '24px', right: '24px' }}>
      <div
        className="sherman-bubble"
        style={{ fontFamily: SHERMAN_FONT_STACK }}>
        <div className="sherman-bubble-inner">
          {bubbleText ? (
            <p className="sherman-text">{bubbleText}</p>
          ) : (
            <TypewriterText
              key={`login-${step}`}
              text={current.text}
              onDone={() => setTextDone(true)} />
          )}
          {textDone && (
            <div className="sherman-buttons">
              {!isTransferMode && step === 0 ? (
                <>
                  <button type="button" className="sherman-btn sherman-btn-dismiss" onClick={dismiss}>
                    {current.options.dismiss}
                  </button>
                  <button type="button" className="sherman-btn sherman-btn-guide" onClick={showWhySftp}>
                    {current.options.guide}
                  </button>
                </>
              ) : (
                <button type="button" className="sherman-btn sherman-btn-guide" onClick={dismiss}>
                  {current.options.dismiss}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="sherman-tail sherman-tail-right" />
      </div>
      <div className={`sherman-character ${idleClass}`} title="Builder Sherman">
        <img src={shermanSrc} alt="Builder Sherman" className="sherman-img" draggable={false} />
      </div>
    </div>
  );
};

const TourSherman = ({ onNavigate, currentPage, isTransferMode }) => {
  const [visible, setVisible] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [touring, setTouring] = useState(false);
  const [textDone, setTextDone] = useState(false);
  const { idleClass, isGone, showSorry, sorryMessage, recall } = useIdleAnimation(textDone);

  const steps = isTransferMode ? TRANSFER_TOUR_STEPS : TOUR_STEPS;

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* */ }
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { setTextDone(false); }, [tourStep]);

  const dismiss = useCallback(() => {
    recall();
    setVisible(false);
    setTouring(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* */ }
  }, [recall]);

  const startTour = useCallback(() => {
    recall();
    setTouring(true);
    setTourStep(1);
    const step = steps[1];
    if (step?.page && onNavigate) onNavigate(step.page);
  }, [onNavigate, steps, recall]);

  const nextStep = useCallback(() => {
    recall();
    const next = tourStep + 1;
    if (next >= steps.length) {
      dismiss();
      return;
    }
    setTourStep(next);
    const step = steps[next];
    if (step?.page && onNavigate && step.page !== currentPage) {
      onNavigate(step.page);
    }
  }, [tourStep, onNavigate, currentPage, dismiss, steps, recall]);

  const prevStep = useCallback(() => {
    recall();
    const prev = tourStep - 1;
    if (prev < 1) return;
    setTourStep(prev);
    const step = steps[prev];
    if (step?.page && onNavigate && step.page !== currentPage) {
      onNavigate(step.page);
    }
  }, [tourStep, onNavigate, currentPage, steps, recall]);

  const step = steps[tourStep];
  useHighlight(visible ? step.highlight : null);

  if (!visible) return null;

  const bubbleText = isGone ? '...' : showSorry ? sorryMessage : null;

  return (
    <div
      className="fixed z-50 flex items-end gap-4"
      style={{ bottom: '24px', right: '24px' }}>
      <div
        className="sherman-bubble"
        style={{ fontFamily: SHERMAN_FONT_STACK }}>
        <div className="sherman-bubble-inner">
          {bubbleText ? (
            <p className="sherman-text">{bubbleText}</p>
          ) : (
            <TypewriterText
              key={`step-${tourStep}`}
              text={step.text}
              onDone={() => setTextDone(true)} />
          )}
          {textDone && (
            <div className="sherman-buttons">
              {!touring && step.options ? (
                <>
                  {step.options.guide ? (
                    <>
                      <button type="button" className="sherman-btn sherman-btn-dismiss" onClick={dismiss}>
                        {step.options.dismiss}
                      </button>
                      <button type="button" className="sherman-btn sherman-btn-guide" onClick={startTour}>
                        {step.options.guide}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="sherman-btn sherman-btn-guide" onClick={dismiss}>
                      {step.options.dismiss}
                    </button>
                  )}
                </>
              ) : touring && step.options?.dismiss && !step.options?.guide ? (
                <button type="button" className="sherman-btn sherman-btn-guide" onClick={dismiss}>
                  {step.options.dismiss}
                </button>
              ) : touring ? (
                <>
                  {tourStep > 1 && (
                    <button type="button" className="sherman-btn sherman-btn-dismiss" onClick={prevStep}>
                      Back
                    </button>
                  )}
                  <button type="button" className="sherman-btn sherman-btn-guide" onClick={nextStep}>
                    {tourStep === steps.length - 1 ? 'Finish' : 'Next'}
                  </button>
                  <button type="button" className="sherman-btn sherman-btn-skip" onClick={dismiss}>
                    Skip
                  </button>
                </>
              ) : null}
            </div>
          )}
        </div>
        <div className="sherman-tail sherman-tail-right" />
      </div>
      <div className={`sherman-character ${idleClass}`} title="Builder Sherman">
        <img src={shermanSrc} alt="Builder Sherman" className="sherman-img" draggable={false} />
      </div>
    </div>
  );
};

const BuilderSherman = ({ onNavigate, currentPage, isLoginScreen, isTransferMode, isDesktopMode }) => {
  if (isLoginScreen) return <LoginSherman isTransferMode={isTransferMode} isDesktopMode={isDesktopMode} />;
  return <TourSherman onNavigate={onNavigate} currentPage={currentPage} isTransferMode={isTransferMode} />;
};

export default BuilderSherman;
